"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import type { DemoScript, DemoStep } from "../types";

interface UseDemoPlayerOptions {
  script: DemoScript;
  /** If false, does not auto-advance — pauses the player */
  autoPlay?: boolean;
  /**
   * Chamado quando o último passo termina. Sem ouvinte o roteiro volta ao
   * começo (o laço de sempre); com ouvinte, quem monta o player decide o que
   * vem depois — na home, a próxima aba.
   */
  onComplete?: () => void;
}

interface UseDemoPlayerReturn {
  currentStep: DemoStep;
  currentIndex: number;
  isLastStep: boolean;
  progress: number; // 0–100, percentage of current durationMs elapsed
  goToStep: (index: number) => void;
  goNext: () => void;
  goPrev: () => void;
  restart: () => void;
}

export function useDemoPlayer({
  script,
  autoPlay = true,
  onComplete,
}: UseDemoPlayerOptions): UseDemoPlayerReturn {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Em ref para o intervalo não ser recriado a cada render do consumidor.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  /*
   * O índice pode estar além do fim logo depois de trocar de roteiro: o estado
   * só volta a zero no efeito abaixo, e o render acontece antes dele. Sem esta
   * trava, `script[currentIndex]` vem `undefined` e a leitura de `durationMs`
   * derruba a seção.
   */
  const safeIndex = Math.min(currentIndex, script.length - 1);
  const currentStep = script[safeIndex];
  const isLastStep = safeIndex === script.length - 1;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i < script.length - 1 ? i + 1 : 0));
    setProgress(0);
    startTimeRef.current = Date.now();
  }, [script.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : 0));
    setProgress(0);
    startTimeRef.current = Date.now();
  }, []);

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < script.length) {
        setCurrentIndex(index);
        setProgress(0);
        startTimeRef.current = Date.now();
      }
    },
    [script.length],
  );

  const restart = useCallback(() => {
    setCurrentIndex(0);
    setProgress(0);
    startTimeRef.current = Date.now();
  }, []);

  // Trocar de roteiro recomeça do primeiro passo: o índice do anterior não
  // corresponde a nada no novo.
  useEffect(() => {
    setCurrentIndex(0);
    setProgress(0);
    startTimeRef.current = Date.now();
  }, [script]);

  useEffect(() => {
    if (!autoPlay) return;

    clearTimer();
    startTimeRef.current = Date.now();
    setProgress(0);

    const tick = 50;
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min((elapsed / currentStep.durationMs) * 100, 100);
      setProgress(pct);

      if (elapsed >= currentStep.durationMs) {
        if (isLastStep && onCompleteRef.current) {
          // O relógio para aqui: quem ouve troca o roteiro, e o efeito volta a
          // rodar já no passo 0 do próximo.
          clearTimer();
          onCompleteRef.current();
        } else {
          goNext();
        }
      }
    }, tick);

    return clearTimer;
  }, [
    currentIndex,
    autoPlay,
    currentStep.durationMs,
    goNext,
    clearTimer,
    isLastStep,
  ]);

  return {
    currentStep,
    currentIndex: safeIndex,
    isLastStep,
    progress,
    goToStep,
    goNext,
    goPrev,
    restart,
  };
}
